// Pure helpers for the production Fill Plan page.
//
// Spec source: Scott/PJ CPLEX meeting 2026-06-26 (meeting d77ba499) + the
// 2026-06-29 addendum (PJ + Scott + Adam), both recorded on kanban bb4492c8 /
// cd779dd6:
//  - rows = finished goods (SKU-aggregated), NO customer columns;
//  - columns = "Day 1 fill" .. "Day 5 fill";
//  - group by department; sort desc by order score, where a row's priority is
//    the MAX score across its contributing orders (decided: MAX, not sum/avg);
//  - a 'fill' is bulk-tank -> package work only — already-filled/staged lines
//    are not emitted by the model, and stock allocations carry no fill units,
//    so rows with zero 5-day fill are excluded.

import type {
  BusinessDay,
  FillPlanFilterState,
  FillPlanSku,
  FillSummaryLongRow,
} from './types';

// ---------------------------------------------------------------------------
// Raw-table access
// ---------------------------------------------------------------------------

/** The 5_day pass is the operating plan — the only pass whose fills are real
 * run instructions (full/no_pos/solon_only are scenario passes). */
export function planRows(
  resultTables: Record<string, unknown> | null | undefined,
): FillSummaryLongRow[] {
  const raw = resultTables?.['mfg_and_fill_summary_long'];
  if (!Array.isArray(raw)) return [];
  return (raw as FillSummaryLongRow[]).filter(
    (r) => r['Model Type'] === '5_day',
  );
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const dateOrNull = (v: unknown): string | null => {
  const s = String(v ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

/** Trailing "(CODE)" of a SKU/order label. */
export function trailingParen(label: string): string {
  const m = label.match(/\(([^()]*)\)\s*$/);
  return m ? m[1] : '';
}

export function stripTrailingParen(label: string): string {
  return label.replace(/\s*\([^()]*\)\s*$/, '').trim();
}

// ---------------------------------------------------------------------------
// Business days (holiday-aware)
// ---------------------------------------------------------------------------

// US federal holidays as observed dates for a given year, matching the
// pandas USFederalHolidayCalendar the model uses to lay out day 1..5.
function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  const d = new Date(Date.UTC(year, month, 1));
  let count = 0;
  while (true) {
    if (d.getUTCDay() === weekday) {
      count += 1;
      if (count === n) return d;
    }
    d.setUTCDate(d.getUTCDate() + 1);
  }
}
function lastWeekday(year: number, month: number, weekday: number): Date {
  const d = new Date(Date.UTC(year, month + 1, 0)); // last day of month
  while (d.getUTCDay() !== weekday) d.setUTCDate(d.getUTCDate() - 1);
  return d;
}
function observed(year: number, month: number, day: number): Date {
  const d = new Date(Date.UTC(year, month, day));
  if (d.getUTCDay() === 6) d.setUTCDate(d.getUTCDate() - 1); // Sat -> Fri
  if (d.getUTCDay() === 0) d.setUTCDate(d.getUTCDate() + 1); // Sun -> Mon
  return d;
}
const isoOf = (d: Date) => d.toISOString().slice(0, 10);

export function usFederalHolidays(year: number): Set<string> {
  const days: Date[] = [
    observed(year, 0, 1), // New Year's Day
    nthWeekday(year, 0, 1, 3), // MLK — 3rd Mon Jan
    nthWeekday(year, 1, 1, 3), // Presidents — 3rd Mon Feb
    lastWeekday(year, 4, 1), // Memorial — last Mon May
    observed(year, 5, 19), // Juneteenth
    observed(year, 6, 4), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor — 1st Mon Sep
    nthWeekday(year, 9, 1, 2), // Columbus — 2nd Mon Oct
    observed(year, 10, 11), // Veterans
    nthWeekday(year, 10, 4, 4), // Thanksgiving — 4th Thu Nov
    observed(year, 11, 25), // Christmas
  ];
  return new Set(days.map(isoOf));
}

/** The plan's Day 1..n as calendar dates, starting at the run date and
 * skipping weekends + observed US federal holidays (e.g. a Thu Jul 2 2026 run
 * has Day 2 = Mon Jul 6, because Fri Jul 3 is the observed July 4th). */
export function businessDays(startIso: string, n = 5): BusinessDay[] {
  const out: BusinessDay[] = [];
  const cur = new Date(`${startIso}T00:00:00Z`);
  const holidays = new Set([
    ...usFederalHolidays(cur.getUTCFullYear()),
    ...usFederalHolidays(cur.getUTCFullYear() + 1),
  ]);
  while (out.length < n) {
    const dow = cur.getUTCDay();
    const iso = isoOf(cur);
    if (dow !== 0 && dow !== 6 && !holidays.has(iso)) {
      out.push({
        n: out.length + 1,
        iso,
        label: cur.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        }),
      });
    }
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

// ---------------------------------------------------------------------------
// SKU aggregation (the table's row model)
// ---------------------------------------------------------------------------

const DAY_KEYS = [
  'day 1 fill',
  'day 2 fill',
  'day 3 fill',
  'day 4 fill',
  'day 5 fill',
] as const;

const MTO_RANK: Record<string, number> = {
  'Stocked (5 bd)': 0,
  'MTO 10': 1,
  'MTO 15': 2,
};

export function aggregateSkus(
  rows: readonly FillSummaryLongRow[],
  orderScores: Record<string, number> | null | undefined,
  todayIso: string,
): FillPlanSku[] {
  const bySku = new Map<string, FillPlanSku>();
  for (const r of rows) {
    const sku = String(r.Sku ?? '').trim();
    if (!sku || sku === '-') continue;
    const dayUnits = [0, ...DAY_KEYS.map((k) => num(r[k]))];
    const lineTotal = num(r['5 day tot fill']);
    // Fill plan = bulk-tank -> package work only. Stock allocations and
    // already-staged lines carry no fill units and stay out of the table.
    if (lineTotal <= 0) continue;

    let agg = bySku.get(sku);
    if (!agg) {
      agg = {
        sku,
        name: stripTrailingParen(sku),
        code: trailingParen(sku),
        department: String(r.Department ?? '-') || '-',
        dayUnits: [0, 0, 0, 0, 0, 0],
        totalUnits: 0,
        orders: [],
        maxScore: null,
        fgInv: num(r['fg inv']),
        fgInvMadison: num(r['fg inv (000)']),
        fgInvSolon: num(r['fg inv (003)']),
        earliestRequiredBy: null,
        daysLate: 0,
        criticalPo: false,
        poLandingDate: null,
        mtoStatus: null,
        fillPeriod: null,
      };
      bySku.set(sku, agg);
    }

    for (let d = 1; d <= 5; d += 1) agg.dayUnits[d] += dayUnits[d];
    agg.totalUnits += lineTotal;

    const orderLabel = String(r.order ?? '').trim();
    const score = orderScores?.[orderLabel];
    const requiredBy = dateOrNull(r['Required By']);
    const criticalPo = String(r['Critical PO'] ?? '') === 'Yes';
    agg.orders.push({
      order: orderLabel,
      units: lineTotal,
      dayUnits,
      requiredBy,
      mtoStatus: r['MTO Status'] ?? null,
      criticalPo,
      score: typeof score === 'number' ? score : null,
    });

    // Row priority = MAX score across contributing orders (2026-06-29
    // decision: MAX, not sum — summing promotes many low-priority orders
    // over one high-priority one).
    if (typeof score === 'number') {
      agg.maxScore = agg.maxScore === null ? score : Math.max(agg.maxScore, score);
    }
    if (requiredBy && (!agg.earliestRequiredBy || requiredBy < agg.earliestRequiredBy)) {
      agg.earliestRequiredBy = requiredBy;
    }
    if (criticalPo) {
      agg.criticalPo = true;
      const land = dateOrNull(r['PO Landing Date']) ?? String(r['PO Landing Date'] ?? '').trim();
      if (land && land !== '-') agg.poLandingDate = land;
    }
    const mto = String(r['MTO Status'] ?? '');
    if (
      mto &&
      (agg.mtoStatus === null || (MTO_RANK[mto] ?? 0) > (MTO_RANK[agg.mtoStatus] ?? 0))
    ) {
      agg.mtoStatus = mto;
    }
    const period = num(r['Fill Period (day 1)']);
    if (period > 0) {
      agg.fillPeriod = agg.fillPeriod === null ? period : Math.min(agg.fillPeriod, period);
    }
  }

  const out = [...bySku.values()];
  for (const s of out) {
    if (s.earliestRequiredBy && s.earliestRequiredBy < todayIso) {
      s.daysLate = Math.round(
        (Date.parse(todayIso) - Date.parse(s.earliestRequiredBy)) / 86_400_000,
      );
    }
    s.orders.sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || b.units - a.units);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Sorting + filtering
// ---------------------------------------------------------------------------

/** True when the run's scores actually differentiate orders (flat 1.0 score
 * files predate the PR #1611 recompute — fall back to the urgency proxy). */
export function scoresAreDifferentiated(
  orderScores: Record<string, number> | null | undefined,
): boolean {
  if (!orderScores) return false;
  const values = Object.values(orderScores);
  return values.length > 1 && new Set(values).size > 1;
}

/** Priority order: real score desc when available, then urgency proxy
 * (days late -> earliest due -> critical PO -> volume). */
export function priorityCompare(a: FillPlanSku, b: FillPlanSku): number {
  const as = a.maxScore ?? -1;
  const bs = b.maxScore ?? -1;
  if (as !== bs) return bs - as;
  if (a.daysLate !== b.daysLate) return b.daysLate - a.daysLate;
  const ad = a.earliestRequiredBy ?? '9999-12-31';
  const bd = b.earliestRequiredBy ?? '9999-12-31';
  if (ad !== bd) return ad < bd ? -1 : 1;
  if (a.criticalPo !== b.criticalPo) return a.criticalPo ? -1 : 1;
  return b.totalUnits - a.totalUnits;
}

export function sortSkus(
  skus: readonly FillPlanSku[],
  sort: FillPlanFilterState['sort'],
): FillPlanSku[] {
  const out = [...skus];
  switch (sort) {
    case 'volume':
      out.sort((a, b) => b.totalUnits - a.totalUnits);
      break;
    case 'due':
      out.sort(
        (a, b) =>
          (a.earliestRequiredBy ?? '9999-12-31').localeCompare(
            b.earliestRequiredBy ?? '9999-12-31',
          ) || b.totalUnits - a.totalUnits,
      );
      break;
    case 'sku':
      out.sort((a, b) => a.name.localeCompare(b.name));
      break;
    default:
      out.sort(priorityCompare);
  }
  return out;
}

export function applyFillPlanFilters(
  skus: readonly FillPlanSku[],
  filters: FillPlanFilterState,
): FillPlanSku[] {
  const q = filters.query.trim().toLowerCase();
  const filtered = skus.filter((s) => {
    if (filters.department && s.department !== filters.department) return false;
    if (filters.day >= 1 && filters.day <= 5 && s.dayUnits[filters.day] <= 0) {
      return false;
    }
    if (q) {
      const hay = `${s.name} ${s.code} ${s.orders.map((o) => o.order).join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  return sortSkus(filtered, filters.sort);
}

/** Departments present, sorted, for the filter dropdown. */
export function departmentsOf(skus: readonly FillPlanSku[]): string[] {
  return [...new Set(skus.map((s) => s.department))].sort();
}

/** Per-day unit totals over a SKU set (index 1..5). */
export function dayTotals(skus: readonly FillPlanSku[]): number[] {
  const totals = [0, 0, 0, 0, 0, 0];
  for (const s of skus) for (let d = 1; d <= 5; d += 1) totals[d] += s.dayUnits[d];
  return totals;
}
